import { AlertCircle, LoaderIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { ProductsUsageChart } from "@/components/admin/product-analytics/ProductsUsageChart";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { productAnalyticsQueries } from "@/api/product-analytics";
import { DateRange } from "@/lib/date";
import { useMemo, useState } from "react";

type SortColumn = "product" | "credits" | "calls" | "avgCreditsPerCall";
type SortDirection = "asc" | "desc";

export function AllProductsAnalytics({ dateRange }: { dateRange: DateRange }) {
  const [sortColumn, setSortColumn] = useState<SortColumn>("credits");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const { data: allProductsSummary, isLoading: isAllProdLoading } = useQuery(
    productAnalyticsQueries.summary(
      dateRange.from?.toISOString(),
      dateRange.to?.toISOString(),
    ),
  );

  function handleSort(col: SortColumn) {
    if (sortColumn === col) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(col);
      setSortDirection("desc");
    }
  }

  const allProdSummaryCards = useMemo(() => {
    if (!allProductsSummary?.summary) return null;
    const sum = allProductsSummary.summary;
    return {
      totalCredits: sum.total_credits_used ?? 0,
      totalCalls: sum.total_api_calls ?? 0,
      avgCreditsPerProduct: sum.avg_credits_per_product ?? 0,
    };
  }, [allProductsSummary]);

  const hasProductData =
    allProductsSummary &&
    Object.entries(allProductsSummary.products).length > 0;

  const sortedAllProductsEntries = useMemo(() => {
    if (!allProductsSummary?.products) return [];
    const arr = Object.entries(allProductsSummary.products).map(
      ([sku, usage]) => {
        const c = usage.total_credits ?? 0;
        const calls = usage.total_calls ?? 0;
        const avg = calls > 0 ? c / calls : 0;
        return {
          sku,
          totalCredits: c,
          totalCalls: calls,
          avgCreditsPerCall: avg,
        };
      },
    );
    arr.sort((a, b) => {
      let aVal: string | number = "";
      let bVal: string | number = "";
      switch (sortColumn) {
        case "product":
          aVal = a.sku;
          bVal = b.sku;
          break;
        case "credits":
          aVal = a.totalCredits;
          bVal = b.totalCredits;
          break;
        case "calls":
          aVal = a.totalCalls;
          bVal = b.totalCalls;
          break;
        case "avgCreditsPerCall":
          aVal = a.avgCreditsPerCall;
          bVal = b.avgCreditsPerCall;
          break;
      }
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [allProductsSummary, sortColumn, sortDirection]);

  return isAllProdLoading ? (
    <div className="flex items-center justify-center h-[300px]">
      <LoaderIcon className="h-8 w-8 animate-spin" />
    </div>
  ) : allProductsSummary ? (
    <>
      {/* Cards for overall summary */}
      {allProdSummaryCards && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <CardContent className="p-0">
              <h4 className="text-sm font-medium text-muted-foreground">
                Total Revenue
              </h4>
              <div className="mt-2 flex items-center justify-between">
                <p className="text-2xl font-bold font-mono">
                  ${(allProdSummaryCards.totalCredits / 100).toFixed(0)}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="p-4">
            <CardContent className="p-0">
              <h4 className="text-sm font-medium text-muted-foreground">
                Total Credits Used
              </h4>
              <div className="mt-2 flex items-center justify-between">
                <p className="text-2xl font-bold font-mono">
                  {allProdSummaryCards.totalCredits.toLocaleString()}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="p-4">
            <CardContent className="p-0">
              <h4 className="text-sm font-medium text-muted-foreground">
                Total Calls
              </h4>
              <div className="mt-2 flex items-center justify-between">
                <p className="text-2xl font-bold font-mono">
                  {allProdSummaryCards.totalCalls.toLocaleString()}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="p-4">
            <CardContent className="p-0">
              <h4 className="text-sm font-medium text-muted-foreground">
                Avg Credits/Prod
              </h4>
              <div className="mt-2 flex items-center justify-between">
                <p className="text-2xl font-bold font-mono">
                  {allProdSummaryCards.avgCreditsPerProduct.toFixed(2)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Grouped Bar Chart */}
      <Card className="p-4 mb-6">
        <h3 className="text-lg font-semibold mb-4">
          All Products Usage (Grouped Bar)
        </h3>
        {hasProductData ? (
          <ProductsUsageChart dateRange={dateRange} />
        ) : (
          <div className="h-[400px] flex items-center justify-center text-muted-foreground">
            <AlertCircle className="h-8 w-8 mr-2" />
            No usage data
          </div>
        )}
      </Card>

      {/* All Products Table */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold mb-4">All Products Summary</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="cursor-pointer"
                onClick={() => handleSort("product")}
              >
                Product
              </TableHead>
              <TableHead
                className="text-right cursor-pointer"
                onClick={() => handleSort("credits")}
              >
                Total Credits
              </TableHead>
              <TableHead
                className="text-right cursor-pointer"
                onClick={() => handleSort("calls")}
              >
                Total Calls
              </TableHead>
              <TableHead
                className="text-right cursor-pointer"
                onClick={() => handleSort("avgCreditsPerCall")}
              >
                Avg Credits/Call
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedAllProductsEntries.map((item) => (
              <TableRow key={item.sku}>
                <TableCell className="font-medium">{item.sku}</TableCell>
                <TableCell className="text-right font-mono">
                  {item.totalCredits.toLocaleString()}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {item.totalCalls.toLocaleString()}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {item.totalCalls > 0
                    ? (item.totalCredits / item.totalCalls).toFixed(2)
                    : "0.00"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </>
  ) : (
    <div className="h-[300px] flex flex-col items-center justify-center text-muted-foreground">
      <AlertCircle className="h-8 w-8 mb-2" />
      <p>No data for selected date range</p>
    </div>
  );
}
